import { parseRoadMap } from '@/components/roadmap/utils'

import { TableData } from 'vue'
import { GoodRoad } from '../roadmap/datamodel/types'
import { getGoodRoadName } from '@/types/global'

export interface GoodRoadTableEntry {
    table: TableData
    goodRoads: GoodRoad[]
    goodRoadName: string
}

export function getTableGoodRoads(table: TableData): GoodRoad[] {
    const resultList = table.expand?.resultList || []
    return parseRoadMap(resultList).goodRoads
}

export function formatGoodRoadName(id: number, gameTypeNo: string): string {
    return getGoodRoadName(id, gameTypeNo)
}

export function buildGoodRoadEntry(
    table: TableData
): GoodRoadTableEntry | null {
    const goodRoads = getTableGoodRoads(table)
    if (!goodRoads.length) return null

    return buildTableEntry(table, goodRoads)
}

export function buildTableEntry(
    table: TableData,
    goodRoads: GoodRoad[] = getTableGoodRoads(table)
): GoodRoadTableEntry {
    return {
        table,
        goodRoads,
        goodRoadName: goodRoads.length
            ? formatGoodRoadName(goodRoads[0].id, table.gameTypeNo)
            : ''
    }
}

export function filterGoodRoadTables(
    tables: TableData[]
): GoodRoadTableEntry[] {
    return tables
        .map(buildGoodRoadEntry)
        .filter((entry): entry is GoodRoadTableEntry => entry !== null)
}

export function buildPanelListData(tables: TableData[]): GoodRoadTableEntry[] {
    return tables
        .map((table) => buildTableEntry(table))
        .sort((a, b) => {
            const aIsGoodRoad = a.goodRoads.length > 0 ? 1 : 0
            const bIsGoodRoad = b.goodRoads.length > 0 ? 1 : 0
            if (aIsGoodRoad !== bIsGoodRoad) return bIsGoodRoad - aIsGoodRoad

            if (aIsGoodRoad && bIsGoodRoad) {
                return (
                    b.table.timeStamp - a.table.timeStamp ||
                    a.table.id - b.table.id
                )
            }

            return a.table.id - b.table.id
        })
}

export const GOOD_ROAD_GAME_TYPES = ['G002', 'G003', 'ALL'] as const

export function isGoodRoadGameType(gameTypeNo: string): boolean {
    return gameTypeNo === 'G002' || gameTypeNo === 'G003'
}

export function filterGoodRoadGameTables(tables: TableData[]): TableData[] {
    return tables.filter((table) => isGoodRoadGameType(table.gameTypeNo))
}

export function splitGoodRoadTablesByType(
    tables: TableData[]
): Record<string, TableData[]> {
    return {
        G002: tables.filter((table) => table.gameTypeNo === 'G002'),
        G003: tables.filter((table) => table.gameTypeNo === 'G003'),
        ALL: tables
    }
}

export function countGoodRoadTables(tables: TableData[]): number {
    return filterGoodRoadTables(tables).length
}

export function getMemberSideBetAmounts(
    table: TableData,
    memberId: number
): { player: number; banker: number } {
    let player = 0
    let banker = 0
    const list = table.expand?.betFormList || []

    list.forEach((betInfo) => {
        if (betInfo.memberId !== memberId) return

        const { playName, amount } = betInfo
        if (playName === '庄' || playName === '龙' || playName === '免佣庄') {
            banker += amount
        } else if (playName === '闲' || playName === '虎') {
            player += amount
        }
    })

    return { player, banker }
}

export function mergeTableDetail(
    base: TableData,
    detail: TableData
): TableData {
    const detailExpand = detail.expand || {}
    const baseExpand = base.expand || {}

    return {
        ...base,
        ...detail,
        expand: {
            ...baseExpand,
            ...detailExpand,
            // Keep live bet totals from detail when present (hall list often omits them)
            zhuangAmount:
                detailExpand.zhuangAmount ?? baseExpand.zhuangAmount ?? 0,
            xianAmount: detailExpand.xianAmount ?? baseExpand.xianAmount ?? 0,
            zhuangDuiAmount:
                detailExpand.zhuangDuiAmount ?? baseExpand.zhuangDuiAmount ?? 0,
            xianDuiAmount:
                detailExpand.xianDuiAmount ?? baseExpand.xianDuiAmount ?? 0,
            heAmount: detailExpand.heAmount ?? baseExpand.heAmount ?? 0
        }
    }
}

export function mergeTableBetUpdate(
    base: TableData,
    update: TableData
): TableData {
    const updateExpand = update.expand || base.expand
    const betFormList =
        updateExpand.betFormList !== undefined &&
        updateExpand.betFormList !== null
            ? updateExpand.betFormList
            : base.expand?.betFormList ?? []

    return {
        ...base,
        expand: {
            ...base.expand,
            betFormList,
            amount:
                updateExpand.amount !== undefined &&
                updateExpand.amount !== null
                    ? updateExpand.amount
                    : base.expand?.amount,
            xianAmount: updateExpand.xianAmount ?? base.expand?.xianAmount,
            zhuangAmount:
                updateExpand.zhuangAmount ?? base.expand?.zhuangAmount,
            xianDuiAmount:
                updateExpand.xianDuiAmount ?? base.expand?.xianDuiAmount,
            zhuangDuiAmount:
                updateExpand.zhuangDuiAmount ?? base.expand?.zhuangDuiAmount,
            heAmount: updateExpand.heAmount ?? base.expand?.heAmount
        }
    }
}

export function mergeMultiDetailIntoTables(
    hallTables: TableData[],
    detailTables: TableData[]
): TableData[] {
    if (!detailTables.length) return hallTables

    const detailById = new Map(detailTables.map((table) => [table.id, table]))

    return hallTables.map((table) => {
        const detail = detailById.get(table.id)
        return detail ? mergeTableDetail(table, detail) : table
    })
}

export function collectGoodRoadTableIds(
    desksByType: Record<string, TableData[]>
): number[] {
    const ids = new Set<number>()

    ;(['G002', 'G003'] as const).forEach((gameTypeNo) => {
        const tables = desksByType[gameTypeNo] || []
        tables.forEach((table) => {
            ids.add(table.id)
        })
    })

    return Array.from(ids)
}
